import { Injectable, inject } from '@angular/core';
import { SubscriptionSignupByUuidService } from '@fitness-force/api';
import { environment } from '@fitness-force/environments';
import type { GetOrderStatusOfQuotation } from '@fitness-force/models';
import { Subscription, interval, take } from 'rxjs';
import {
  evaluateSignupPaymentPollOutcome,
  type SignupPaymentPollOutcome,
} from './signup-payment-poll.util';
import {
  PAYMENT_CONFIRMATION_MAX_POLL_COUNT,
  PAYMENT_CONFIRMATION_POLL_INTERVAL_MS,
} from './payment-polling.constants';

export type SignupPaymentPollTerminalOutcome = SignupPaymentPollOutcome | 'timeout';

export interface PollSignupPaymentOptions {
  companyUuid: string;
  tenantUuid: string;
  uuid: string;
  /** Hard stop for polling (e.g. POS Tap to Pay 2-minute window). */
  deadlineMs?: number;
  /** Card-on-file flows must wait for online_payment_status instead of signup status. */
  requireOnlinePaymentStatus?: boolean;
  onOutcome: (
    outcome: SignupPaymentPollTerminalOutcome,
    response?: GetOrderStatusOfQuotation
  ) => void;
}

@Injectable({
  providedIn: 'root',
})
export class OrderSubscriptionStatusPollingService {
  private readonly subscriptionSignUp = inject(SubscriptionSignupByUuidService);

  private pollingSubscription?: Subscription;
  private pollCount = 0;
  private pollingTerminated = false;
  private deadlineTimerId?: ReturnType<typeof setTimeout>;
  private activeOptions?: PollSignupPaymentOptions;
  private onTerminalOutcome?: (
    outcome: SignupPaymentPollTerminalOutcome,
    response?: GetOrderStatusOfQuotation
  ) => void;
  private lastPollingTime = 0;
  private visibilityListenerAttached = false;
  private readonly pollingInterval = PAYMENT_CONFIRMATION_POLL_INTERVAL_MS;
  private readonly maxPollCount = PAYMENT_CONFIRMATION_MAX_POLL_COUNT;

  private readonly handleVisibilityChange = (): void => {
    if (!this.activeOptions || this.pollingTerminated) {
      return;
    }

    if (document.hidden) {
      this.pausePolling();
      return;
    }

    const timeSinceLastPoll = Date.now() - this.lastPollingTime;
    if (this.lastPollingTime > 0 && timeSinceLastPoll >= this.pollingInterval) {
      this.fetchStatus();
    }

    if (this.pollCount < this.maxPollCount) {
      this.startIntervalPolling();
    }
  };

  isPollingActive(): boolean {
    return !!this.pollingSubscription || !!this.activeOptions;
  }

  stopPolling(): void {
    this.detachVisibilityListener();
    this.unsubscribePolling();
    this.clearDeadlineTimer();
    this.pollCount = 0;
    this.pollingTerminated = false;
    this.activeOptions = undefined;
    this.onTerminalOutcome = undefined;
    this.lastPollingTime = 0;
  }

  pollSignupPaymentUntilTerminal(options: PollSignupPaymentOptions): void {
    this.stopPolling();
    this.pollingTerminated = false;
    this.activeOptions = options;
    this.lastPollingTime = Date.now();

    this.onTerminalOutcome = (outcome, response) => {
      if (this.pollingTerminated) {
        return;
      }
      this.pollingTerminated = true;
      this.stopPolling();
      options.onOutcome(outcome, response);
    };

    if (options.deadlineMs && options.deadlineMs > 0) {
      this.deadlineTimerId = setTimeout(() => {
        this.onTerminalOutcome?.('timeout');
      }, options.deadlineMs);
    }

    this.attachVisibilityListener();
    this.startPollingLoop();
  }

  private startPollingLoop(): void {
    if (this.pollingTerminated || !this.activeOptions || document.hidden) {
      return;
    }

    if (this.pollCount < this.maxPollCount) {
      this.fetchStatus();
    }

    this.startIntervalPolling();
  }

  private startIntervalPolling(): void {
    if (
      this.pollingSubscription ||
      document.hidden ||
      this.pollingTerminated ||
      !this.activeOptions
    ) {
      return;
    }

    this.pollingSubscription = interval(this.pollingInterval)
      .pipe(take(this.maxPollCount - this.pollCount))
      .subscribe({
        next: () => {
          if (document.hidden) {
            this.pausePolling();
            return;
          }

          this.pollCount++;
          this.fetchStatus();

          if (this.pollCount >= this.maxPollCount) {
            this.onTerminalOutcome?.('timeout');
          }
        },
        complete: () => {
          if (!this.pollingTerminated) {
            this.onTerminalOutcome?.('timeout');
          } else {
            this.unsubscribePolling();
          }
        },
      });
  }

  private fetchStatus(): void {
    const options = this.activeOptions;
    if (!options || this.pollingTerminated) {
      return;
    }

    this.lastPollingTime = Date.now();

    this.subscriptionSignUp
      .GetOrderSubscriptionStatus(
        environment.BASE_URL,
        options.companyUuid,
        options.tenantUuid,
        options.uuid
      )
      .subscribe({
        next: (response) => {
          const outcome = evaluateSignupPaymentPollOutcome(response, {
            requireOnlinePaymentStatus: options.requireOnlinePaymentStatus,
          });
          if (outcome === 'success' || outcome === 'failed') {
            this.onTerminalOutcome?.(outcome, response);
          }
        },
      });
  }

  private pausePolling(): void {
    this.unsubscribePolling();
    this.lastPollingTime = Date.now();
  }

  private unsubscribePolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = undefined;
    }
  }

  private clearDeadlineTimer(): void {
    if (this.deadlineTimerId) {
      clearTimeout(this.deadlineTimerId);
      this.deadlineTimerId = undefined;
    }
  }

  private attachVisibilityListener(): void {
    if (this.visibilityListenerAttached) {
      return;
    }

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityListenerAttached = true;
  }

  private detachVisibilityListener(): void {
    if (!this.visibilityListenerAttached) {
      return;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.visibilityListenerAttached = false;
  }
}
